import type { ReactNode } from 'react'
import { Link, Navigate, useLocation } from '@tanstack/react-router'
import { LogOutIcon, ShieldAlertIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { ButtonLink } from '@/components/ui/button-link'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useSession } from '@/lib/auth-client'
import { env } from '@/lib/env'
import { useSignOut } from '@/lib/use-sign-out'
import { useAdminMe } from '@/features/shared/hooks/use-admin-me'
import { AdminShell } from '@/features/shared/components/admin-shell'
import { AdminFormSkeleton } from '@/features/shared/components/admin-form-skeleton'
import { AdminListSkeleton } from '@/features/shared/components/admin-list-skeleton'
import {
  type AdminPendingShape,
  pendingShapeForPath,
} from '@/features/shared/lib/admin-nav'

/**
 * Gates the ops app for moderator/admin only.
 */
export function AdminGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { data: session, isPending: sessionPending } = useSession()
  const { data: me, isPending: mePending, isError } = useAdminMe()

  if (sessionPending || (session && mePending)) {
    return <PendingShell shape={pendingShapeForPath(location.pathname)} />
  }

  if (!session) {
    return (
      <Navigate
        to="/login"
        search={{
          returnTo: `${location.pathname}${location.searchStr}${location.hash}`,
        }}
        replace
      />
    )
  }

  if (isError || !me) {
    return (
      <UnauthorizedGate
        identity={session.user?.email ?? session.user?.name ?? null}
      />
    )
  }

  return (
    <AdminShell
      isAdmin={me.role === 'admin' || me.role === 'superadmin'}
      user={session.user}
    >
      {children}
    </AdminShell>
  )
}

function UnauthorizedGate({ identity }: { identity: string | null }) {
  const { signOut, isSigningOut } = useSignOut()

  return (
    <GateFrame>
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 inline-flex size-10 items-center justify-center rounded-xl border border-border bg-destructive/10 text-destructive">
            <ShieldAlertIcon className="size-5" aria-hidden />
          </div>
          <CardTitle className="font-display text-xl">
            No staff access
          </CardTitle>
          <CardDescription className="mt-2 leading-relaxed">
            {identity ? (
              <>
                You are signed in as{' '}
                <span className="font-medium text-foreground">{identity}</span>,
                but that
              </>
            ) : (
              'Your'
            )}{' '}
            account has no moderator or admin role. Ask an admin to grant one,
            then retry.
          </CardDescription>
        </CardHeader>
        <div className="flex flex-col gap-2 border-t border-border px-6 py-4 sm:flex-row sm:flex-wrap">
          <Link
            to="/"
            className="inline-flex h-8 flex-1 items-center justify-center gap-2 rounded-lg border border-input bg-popover px-3 text-sm font-medium shadow-xs/5 hover:bg-accent/50"
          >
            Retry
          </Link>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="flex-1"
            disabled={isSigningOut}
            onClick={() => {
              void signOut()
            }}
          >
            <LogOutIcon className="size-3.5" aria-hidden />
            {isSigningOut ? 'Signing out…' : 'Sign out'}
          </Button>
          <ButtonLink
            href={env.productUrl}
            variant="secondary"
            className="flex-1 justify-center sm:basis-full"
          >
            Back to Harmoni
          </ButtonLink>
        </div>
      </Card>
    </GateFrame>
  )
}

/**
 * Session-pending chrome. Mirrors {@link AdminShell} geometry — sidebar rail,
 * page header, max-w-7xl body — so the destination does not jump once the
 * staff session resolves (#1054).
 */
function PendingShell({ shape }: { shape: AdminPendingShape }) {
  return (
    <div
      className="relative flex min-h-svh bg-background"
      role="status"
      aria-label="Checking staff session…"
      data-pending-shape={shape}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-50 h-1 bg-gradient-to-r from-primary via-signal to-primary/70"
        aria-hidden
      />

      <div
        className="hidden w-60 shrink-0 flex-col gap-4 border-e border-border p-3 md:flex"
        aria-hidden
      >
        <Skeleton className="h-8 w-32" />
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full rounded-lg" />
          ))}
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-7xl min-w-0 flex-1 flex-col gap-4 px-4 py-5 sm:px-6 lg:px-8">
        <div className="space-y-2" aria-hidden>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
        {/* Nested skeletons carry their own role=status — hide them so the
            shell announces once. */}
        <div className="flex min-h-0 flex-1 flex-col" aria-hidden>
          <PendingBody shape={shape} />
        </div>
      </div>

      <span className="sr-only">Checking staff session…</span>
    </div>
  )
}

function PendingBody({ shape }: { shape: AdminPendingShape }) {
  if (shape === 'queue') {
    return (
      <div className="grid min-h-[min(70vh,40rem)] flex-1 gap-3 lg:min-h-0 lg:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)] lg:gap-4">
        <div className="flex flex-col gap-2 overflow-hidden rounded-xl border border-border bg-card p-3">
          <Skeleton className="h-8 w-full rounded-lg" />
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
        <div className="flex flex-col gap-3 overflow-hidden rounded-xl border border-border bg-card p-4">
          <Skeleton className="h-6 w-56 max-w-full" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="min-h-40 w-full flex-1 rounded-lg" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-24 rounded-lg" />
            <Skeleton className="h-8 w-32 rounded-lg" />
          </div>
        </div>
      </div>
    )
  }

  if (shape === 'table') return <AdminListSkeleton rows={8} />
  if (shape === 'form') return <AdminFormSkeleton />

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-2xl" />
        ))}
      </div>
      <Skeleton className="h-40 w-full rounded-2xl" />
    </div>
  )
}

function GateFrame({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center bg-background p-6">
      <div
        className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-signal to-primary/70"
        aria-hidden
      />
      <span className="mb-5 font-mono text-[11px] font-medium tracking-[0.18em] text-muted-foreground uppercase">
        Harmoni ops
      </span>
      {children}
    </div>
  )
}
